/* eslint-disable react-hooks/exhaustive-deps */
import { useParams, useNavigate } from "react-router-dom";
import { ReactComponent as LeftArrow } from "../../assets/icons/arrows/left-arrow.svg";
import { useEffect } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useBeevisionAuditLogs } from "../../store/useBeevisionAuditLogs";
import dayjs from "dayjs";

function BeevisionAuditLogDetails() {
  /** Hooks */
  const navigate = useNavigate();
  const params = useParams();
  const {
    getLogById,
    selectedLog,
    setSelectedLog,
    measurementData,
    clearMeasurementData,
    isMeasurementDataLoading,
  } = useBeevisionAuditLogs();

  /** Effects */
  useEffect(() => {
    if (!params?.id) return;
    getLogById(params?.id);
    return () => {
      setSelectedLog(null);
      clearMeasurementData();
    };
  }, [params?.id, getLogById, setSelectedLog, clearMeasurementData]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  if (!selectedLog) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex flex-row items-center mb-6 justify-between">
        <div className="flex flex-row items-center justify-start">
          <LeftArrow onClick={goBack} className="mr-3 cursor-pointer" />
          <h1 className="font-satoshi text-base sm:text-2xl font-bold text-dark">
            #{params?.id}
          </h1>
        </div>
      </div>
      {selectedLog.user && (
        <div className="w-full sm:w-2/3 min-w-fit bg-white border border-gray-border drop-shadow-md mb-[20px]">
          <div className="w-full bg-gray-50 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-medium text-base">
            <p className="flex flex-1">User Details</p>
          </div>
          <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
            <p className="flex flex-1">Name</p>
            <p className="flex flex-1 justify-end">
              {selectedLog.user.first_name} {selectedLog.user.last_name}
            </p>
          </div>
        </div>
      )}
      <div className="w-full sm:w-2/3 min-w-fit bg-white border border-gray-border drop-shadow-md mb-[20px]">
        <div className="w-full bg-gray-50 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-medium text-base">
          <p className="flex flex-1">Details</p>
        </div>
        <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
          <p className="flex flex-1">ID</p>
          <p className="flex flex-1 justify-end">{selectedLog._id}</p>
        </div>
        <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
          <p className="flex flex-1">Status</p>
          <p className="flex flex-1 justify-end">
            <span
              className={`font-bold ${
                selectedLog.status === "approved"
                  ? "text-green-500"
                  : selectedLog.status === "rejected" ||
                      selectedLog.status === "webhook_rejected"
                    ? "text-red-500"
                    : selectedLog.status === "pending"
                      ? "text-orange-500"
                      : "text-gray-800"
              }`}
            >
              {selectedLog.status.charAt(0).toUpperCase() +
                selectedLog.status.slice(1)}
            </span>
          </p>
        </div>
        <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
          <p className="flex flex-1">Created At</p>
          <p className="flex flex-1 justify-end">
            {dayjs(selectedLog.createdAt).format("DD/MM/YYYY HH:mm:ss")}
          </p>
        </div>
        <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
          <p className="flex flex-1">Updated At</p>
          <p className="flex flex-1 justify-end">
            {dayjs(selectedLog.updatedAt).format("DD/MM/YYYY HH:mm:ss")}
          </p>
        </div>
        {selectedLog.rejectionReason && (
          <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
            <p className="flex flex-1">Rejection Reason</p>
            <p className="flex flex-1 justify-end">
              {selectedLog.rejectionReason}
            </p>
          </div>
        )}
        {selectedLog?.dropData?.weight && (
          <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
            <p className="flex flex-1">Suggested weight for this drop</p>
            <p className="flex flex-1 justify-end">
              {new Intl.NumberFormat("nl-NL", {
                style: "unit",
                unit: "kilogram",
                unitDisplay: "short",
              }).format(Number(selectedLog?.dropData?.weight ?? 0))}
            </p>
          </div>
        )}
        {selectedLog?.dropData?.totalPrice && (
          <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
            <p className="flex flex-1">Suggested price for this drop</p>
            <p className="flex flex-1 justify-end">
              {new Intl.NumberFormat("nl-NL", {
                style: "currency",
                currency: "EUR",
              }).format(Number(selectedLog?.dropData?.totalPrice ?? 0) / 100)}
            </p>
          </div>
        )}
        {(selectedLog?.dropData as any)?.marketPriceData?.name && (
          <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
            <p className="flex flex-1">Final material</p>
            <p className="flex flex-1 justify-end">
              {(selectedLog?.dropData as any)?.marketPriceData?.name}
            </p>
          </div>
        )}
        <div className="w-full bg-white hover:bg-gray-100 px-10 py-4 flex flex-col font-satoshi text-dark font-normal text-base">
          <p className="mb-2">Webhook Data</p>
          <pre className="bg-gray-100 p-4 rounded-md overflow-auto text-sm">
            {JSON.stringify(selectedLog.webhookData, null, 2)}
          </pre>
        </div>
      </div>
      {isMeasurementDataLoading ? (
        <div className="w-full sm:w-2/3 min-w-fit bg-white border border-gray-border drop-shadow-md mb-[20px]">
          <div className="w-full bg-gray-50 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-medium text-base">
            <p className="flex flex-1">Measurement Image</p>
          </div>
          <div className="w-full bg-white p-10 flex justify-center">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        </div>
      ) : (
        measurementData?.results?.[0]?.ImageBase64 && (
          <div className="w-full sm:w-2/3 min-w-fit bg-white border border-gray-border drop-shadow-md mb-[20px]">
            <div className="w-full bg-gray-50 px-10 py-4 flex flex-row items-center justify-between font-satoshi text-dark font-medium text-base">
              <p className="flex flex-1">Measurement Image</p>
            </div>
            <div className="w-full bg-white p-10 flex justify-center">
              <img
                src={`data:image/jpeg;base64,${measurementData?.results?.[0]?.ImageBase64}`}
                alt="Measurement"
              />
            </div>
          </div>
        )
      )}
    </div>
  );
}

export default withProtectedRoute(BeevisionAuditLogDetails);
